"use client";

import { useEffect, useRef, useCallback } from "react";

/** Cloudflare's official always-passes test keys — used for local dev. */
const TURNSTILE_TEST_SITEKEYS = [
  "1x00000000000000000000AA", // always passes
  "2x00000000000000000000AB", // always blocks
  "3x00000000000000000000FF", // forces interactive
];

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "compact";
        },
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

interface TurnstileProps {
  onToken: (token: string) => void;
  onExpired?: () => void;
  /** Called when the Turnstile script fails to load (e.g. network issue, ad blocker) */
  onError?: () => void;
  /**
   * Override the site key. When omitted, falls back to the site-wide
   * `NEXT_PUBLIC_TURNSTILE_SITE_KEY` used by auth flows. Pass a dedicated
   * key (e.g. `NEXT_PUBLIC_TURNSTILE_TOOLS_SITE_KEY`) to gate a single
   * page without enabling captcha site-wide.
   */
  sitekey?: string;
}

export default function Turnstile({ onToken, onExpired, onError, sitekey }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const resolvedSitekey = sitekey || process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "";

  // In local dev without a real site key, skip the widget entirely and
  // auto-fire a dummy token so forms remain functional.
  const isDev = process.env.NODE_ENV === "development";
  const noKey = !resolvedSitekey;
  const isTestKey = TURNSTILE_TEST_SITEKEYS.includes(resolvedSitekey);
  const shouldBypass = isDev && (noKey || isTestKey);

  useEffect(() => {
    if (shouldBypass) {
      // Fire a dummy token on next tick so callers treat it like a real pass.
      const t = setTimeout(() => onToken("TURNSTILE_DUMMY_TOKEN_DEV"), 0);
      return () => clearTimeout(t);
    }
  }, [shouldBypass, onToken]);

  const renderWidget = useCallback(() => {
    if (!containerRef.current || !window.turnstile || widgetIdRef.current) return;
    if (!resolvedSitekey) return;

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: resolvedSitekey,
      callback: onToken,
      "expired-callback": () => onExpired?.(),
      "error-callback": () => onExpired?.(),
      theme: "auto",
      size: "normal",
    });
  }, [onToken, onExpired, resolvedSitekey]);

  useEffect(() => {
    if (shouldBypass) return;

    // If Turnstile API is already available, render immediately
    if (window.turnstile) {
      renderWidget();
      return;
    }

    // Register the onload callback — Cloudflare calls this when the API is ready
    window.onTurnstileLoad = () => {
      renderWidget();
    };

    // Only add the script if it's not already in the DOM
    if (!document.querySelector('script[src*="turnstile"]')) {
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad";
      script.async = true;
      script.onerror = () => onError?.();
      document.head.appendChild(script);
    }

    // Fallback: if Turnstile API never becomes available, notify after timeout
    const timeout = setTimeout(() => {
      if (!window.turnstile) onError?.();
    }, 5000);

    return () => {
      clearTimeout(timeout);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [renderWidget, onError, shouldBypass]);

  if (shouldBypass) return null;

  return <div ref={containerRef} className="flex justify-center" />;
}
